import React from 'react';
import * as H from "../../../hooks";
import * as C from "../../../Common";
import { TB, T, TC, LANG } from "../../../Constants";

type NumberProps = {
    /** The unit of the displayed number */
    unit: string;
    /** The label of the number */
    label: string;
    /** Is the component refreshing */
    refreshing?: boolean;
    /** The energy type */
    energy?: string;
    /** The data to display */
    data: ReturnType<T.API.Report.Dashboard.GetESGSectionData>["number"];
    /** Refresh the data of the component */
    refresh: (params?: Parameters<T.API.Report.Dashboard.GetESGSectionData>[0]["other"]) => void;
};

const Number: React.FC<NumberProps> = ({ refresh, ...props }) => {
    const lg = H.useLanguage();
    const local_lang_value = React.useMemo(() => LANG.getHtmlLanguageCode(lg.prop), [lg.prop]);

    const sums = React.useMemo(() => {
        let sum_value = 0, surface_value = 0,
            surface_ytd = 0, sum_ytd = 0;

        if (props.data) {
            props.data.forEach(data => {
                if (data.consumption !== null) {
                    sum_value += data.consumption;
                    surface_value += data.surface;
                }
                if (data.ytd_consumption !== null) {
                    surface_ytd += data.surface;
                    sum_ytd += data.ytd_consumption;
                }
            });

            const sum_ytd_avg = surface_ytd === 0 ? NaN : sum_ytd / surface_ytd;
            const sum_value_avg = surface_value === 0 ? NaN : sum_value / surface_value;
            return { value: sum_value_avg, ytd: sum_ytd_avg };
        }
        return { value: NaN, ytd: NaN };
    }, [props.data]);

    const value = React.useMemo(() => {
        let percentage = NaN;
        let percentage_string = "";

        if (!isNaN(sums.value) && !isNaN(sums.ytd)) percentage = TB.toPercent(sums.value - sums.ytd, sums.ytd);
        if (percentage === Infinity) percentage = NaN;
        if (!isNaN(percentage)) percentage_string = (percentage > 0 ? "+" : "") + TB.formatNumber(percentage, local_lang_value);

        return {
            percent: percentage_string,
            color: percentage > 0 ? 'red' : 'green',
            previous: TB.formatNumber(sums.ytd, local_lang_value),
            current: TB.formatNumber(sums.value, local_lang_value),
        }
    }, [sums]);

    const label = React.useMemo(() => lg.getStaticText(props.label), [lg, props.label]);

    return <div className='esg-graph d-flex flex-column w-100 h-100'>
        <C.Spinner loading={props.refreshing} min_load_size="100%">
            <div className='flex-grow-1 w-100'>
                <div className="h-100 d-flex flex-column justify-content-center align-items-center text-center" style={{ gap: '20px' }}>
                    <div style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>
                        <p style={{ fontSize: '30px' }} className="mb-3 fw-bold">{label} (YTD)</p>
                    </div>
                    <div>
                        <p className="mb-3 fw-bold" style={{ fontSize: '36px', color: 'MediumSlateBlue' }} children={value.current} />
                    </div>
                    <div>
                        <p className="mb-3" style={{ fontSize: '20px', fontWeight: 'bold' }}>
                            {props.unit} / m²
                        </p>
                    </div>
                    <div>
                        <p className="fw-bold" style={{ fontSize: '24px', color: value.color }}>
                            PY : {value.previous}{value.percent ? ` (${value.percent}%)` : ''}
                        </p>
                    </div>
                </div>
            </div>
        </C.Spinner>
    </div>;
}

export default Number;